export interface SessionData {
  token: string;
  customerId: number;
  email: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  expiresAt: number;
  createdAt: number;
}

function key(): string {
  return "session";
}

function randomToken(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function createSession(customerId: number, email: string, username?: string, firstName?: string, lastName?: string, ttlMinutes: number = 180): SessionData {
  const now = Date.now();
  const expiresAt = now + ttlMinutes * 60_000;
  const data: SessionData = {
    token: randomToken(),
    customerId,
    email: email.toLowerCase(),
    username,
    firstName,
    lastName,
    createdAt: now,
    expiresAt,
  };
  localStorage.setItem(key(), JSON.stringify(data));
  return data;
}

export function getSession(): SessionData | null {
  const raw = localStorage.getItem(key());
  if (!raw) return null;
  try {
    const data: SessionData = JSON.parse(raw);
    if (Date.now() > data.expiresAt) {
      clearSession();
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export function isLoggedIn(): boolean {
  return !!getSession();
}

export function clearSession(): void {
  localStorage.removeItem(key());
}

export function getAvatarInitials(email?: string, username?: string, firstName?: string, lastName?: string): string {
  // 1. Prioritize passed names
  let first = (firstName || "").trim();
  let last = (lastName || "").trim();

  // 2. Try session if names are not passed
  if (!first && !last) {
    const session = getSession();
    if (session) {
      first = (session.firstName || "").trim();
      last = (session.lastName || "").trim();
    }
  }

  // 3. Robust Name Extraction
  // If we have both, return initials
  if (first && last) {
    return (first[0] + last[0]).toUpperCase();
  }

  // Handle case where name might be in a single field or needs splitting
  const fullName = first || last || (username && !username.includes("@") ? username : "");
  if (fullName) {
    const parts = fullName.split(/[\s._\-]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  }

  // 4. Fallback to Email
  const source = (email || "User").trim();
  const emailPart = source.split("@")[0];
  const emailParts = emailPart.split(/[\s._\-]+/).filter(Boolean);

  if (emailParts.length >= 2) {
    return (emailParts[0][0] + emailParts[1][0]).toUpperCase();
  }
  return emailPart.substring(0, 2).toUpperCase();
}
